import React, { useEffect, useRef } from 'react';
import { ReactComponent as CheckMark } from '../../../images/check-mark.svg';
import './InputDropdownMenu.scss';

export interface DropdownOption {
  label: string;
  value: string;
}

interface InputDropdownMenuProps {
  title?: string;
  options: DropdownOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  disableClickOutside?: boolean;
}

const InputDropdownMenu: React.FC<InputDropdownMenuProps> = ({
  title = 'Card type',
  options,
  selectedValue,
  onChange,
  isOpen,
  onClose,
  className = '',
  disableClickOutside = false,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disableClickOutside) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, disableClickOutside]);

  const handleOptionClick = (value: string) => {
    onChange(value);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={`input-dropdown-menu ${className}`}
    >
      <div className="input-dropdown-menu__wrapper">
        <div className="input-dropdown-menu__items">
          {/* Header */}
          <div className="input-dropdown-menu__header">
            <p className="input-dropdown-menu__header-text">{title}</p>
          </div>

          {/* Options */}
          <div className="input-dropdown-menu__options">
            {options.map((option) => (
              <div
                key={option.value}
                className="input-dropdown-menu__option-wrapper"
                onClick={() => handleOptionClick(option.value)}
              >
                <div
                  className={`input-dropdown-menu__option-content ${
                    selectedValue === option.value ? 'input-dropdown-menu__option-content--active' : ''
                  }`}
                >
                  <div className="input-dropdown-menu__option-text-wrapper">
                    <div className="input-dropdown-menu__option-text-container">
                      <div className="input-dropdown-menu__option-text-inner">
                        <p className="input-dropdown-menu__option-text">{option.label}</p>
                      </div>
                    </div>
                  </div>

                  {/* Check icon for selected option */}
                  {selectedValue === option.value && (
                    <div className="input-dropdown-menu__check-icon">
                      <CheckMark />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InputDropdownMenu;

